import { tableCellClasses } from '@mui/material'
import { common } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'
import { createDarkFamilyComponents } from '../dark-family/create-components'

export const createComponents = ({ palette }) => {
    const base = createDarkFamilyComponents({
        palette,
        tweaks: {
            appBarAlpha: 0.94,
            paperAlpha: 0.97,
            elevatedSurfaceAlpha: 0.96,
            tableHeadAlpha: 0.05,
            borderAlpha: 0.1
        }
    })

    return {
        ...base,
        MuiAppBar: {
            ...base.MuiAppBar,
            styleOverrides: {
                ...base.MuiAppBar.styleOverrides,
                root: {
                    ...base.MuiAppBar.styleOverrides.root,
                    backgroundColor: alpha(common.black, 0.78),
                    borderBottom: `1px solid ${alpha(palette.primary.main, 0.24)}`,
                    boxShadow: `0px 12px 28px ${alpha(common.black, 0.42)}`,
                    backgroundImage:
                        `linear-gradient(180deg, ${alpha(
                            palette.primary.main,
                            0.12
                        )} 0%, transparent 100%), ` +
                        `linear-gradient(90deg, ${alpha(
                            common.white,
                            0.02
                        )} 0%, transparent 45%, ${alpha(
                            common.white,
                            0.02
                        )} 100%)`
                }
            }
        },
        MuiButton: {
            ...base.MuiButton,
            styleOverrides: {
                ...base.MuiButton.styleOverrides,
                contained: {
                    ...base.MuiButton.styleOverrides.contained,
                    backgroundImage: `linear-gradient(135deg, ${alpha(
                        palette.primary.light,
                        0.9
                    )} 0%, ${palette.primary.main} 38%, ${palette.primary.dark} 100%)`,
                    boxShadow:
                        `0px 0px 0px 1px ${alpha(common.white, 0.06)} inset, ` +
                        `0px 0px 24px ${alpha(palette.primary.main, 0.32)}, ` +
                        `0px 12px 28px ${alpha(common.black, 0.45)}`,
                    '&:hover': {
                        ...base.MuiButton.styleOverrides.contained['&:hover'],
                        boxShadow:
                            `0px 0px 0px 1px ${alpha(common.white, 0.08)} inset, ` +
                            `0px 0px 34px ${alpha(palette.primary.main, 0.38)}, ` +
                            `0px 16px 34px ${alpha(common.black, 0.5)}`
                    }
                },
                outlined: {
                    ...base.MuiButton.styleOverrides.outlined,
                    borderColor: alpha(palette.primary.main, 0.34),
                    backgroundColor: alpha(palette.primary.main, 0.05),
                    '&:hover': {
                        ...base.MuiButton.styleOverrides.outlined['&:hover'],
                        borderColor: alpha(palette.primary.main, 0.52),
                        boxShadow: `0 0 0 1px ${alpha(
                            palette.primary.main,
                            0.16
                        )} inset`
                    }
                },
                text: {
                    ...base.MuiButton.styleOverrides.text,
                    '&:hover': {
                        ...base.MuiButton.styleOverrides.text['&:hover'],
                        boxShadow: `0 0 0 1px ${alpha(
                            palette.primary.main,
                            0.12
                        )} inset`
                    }
                }
            }
        },
        MuiCard: {
            ...base.MuiCard,
            styleOverrides: {
                ...base.MuiCard.styleOverrides,
                root: {
                    ...base.MuiCard.styleOverrides.root,
                    position: 'relative',
                    overflow: 'hidden',
                    borderColor: alpha(palette.primary.main, 0.12),
                    backgroundImage:
                        `radial-gradient(circle at 100% 0%, ${alpha(
                            palette.primary.main,
                            0.14
                        )} 0%, transparent 45%), ` +
                        `linear-gradient(180deg, ${alpha(
                            common.white,
                            0.015
                        )} 0%, transparent 100%)`,
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        borderRadius: 'inherit',
                        background: `linear-gradient(90deg, transparent, ${alpha(
                            common.white,
                            0.04
                        )}, transparent)`,
                        opacity: 0.8
                    },
                    '&:hover': {
                        ...base.MuiCard.styleOverrides.root['&:hover'],
                        borderColor: alpha(palette.primary.main, 0.28),
                        boxShadow:
                            `0px 0px 0px 1px ${alpha(
                                palette.primary.main,
                                0.14
                            )} inset, ` +
                            `0px 0px 40px ${alpha(palette.primary.main, 0.18)}, ` +
                            `0px 22px 48px rgba(0, 0, 0, 0.34)`
                    }
                }
            }
        },
        MuiChip: {
            ...base.MuiChip,
            styleOverrides: {
                ...base.MuiChip.styleOverrides,
                root: {
                    ...base.MuiChip.styleOverrides.root,
                    borderColor: alpha(palette.primary.main, 0.22),
                    backgroundColor: alpha(palette.primary.main, 0.07),
                    boxShadow: `0 0 0 1px ${alpha(palette.primary.main, 0.08)} inset`
                }
            }
        },
        MuiCssBaseline: {
            ...base.MuiCssBaseline,
            styleOverrides: {
                ...base.MuiCssBaseline.styleOverrides,
                body: {
                    ...base.MuiCssBaseline.styleOverrides.body,
                    background: palette.background.default
                },
                '::selection': {
                    backgroundColor: alpha(palette.primary.main, 0.35),
                    color: common.white
                }
            }
        },
        MuiDialog: {
            ...base.MuiDialog,
            styleOverrides: {
                ...base.MuiDialog.styleOverrides,
                paper: {
                    ...base.MuiDialog.styleOverrides.paper,
                    borderColor: alpha(palette.primary.main, 0.2),
                    backgroundImage:
                        `radial-gradient(circle at 100% 0%, ${alpha(
                            palette.primary.main,
                            0.14
                        )} 0%, transparent 50%), ` +
                        `linear-gradient(180deg, ${alpha(
                            common.white,
                            0.015
                        )} 0%, transparent 100%)`
                }
            }
        },
        MuiDrawer: {
            ...base.MuiDrawer,
            styleOverrides: {
                ...base.MuiDrawer.styleOverrides,
                paper: {
                    ...base.MuiDrawer.styleOverrides.paper,
                    borderRight: `1px solid ${alpha(palette.primary.main, 0.18)}`,
                    backgroundImage:
                        `radial-gradient(circle at 100% 0%, ${alpha(
                            palette.primary.main,
                            0.1
                        )} 0%, transparent 45%), ` +
                        `linear-gradient(180deg, ${alpha(
                            common.white,
                            0.012
                        )} 0%, transparent 100%)`
                }
            }
        },
        MuiFilledInput: {
            ...base.MuiFilledInput,
            styleOverrides: {
                ...base.MuiFilledInput.styleOverrides,
                root: {
                    ...base.MuiFilledInput.styleOverrides.root,
                    borderColor: alpha(palette.primary.main, 0.16),
                    backgroundColor: alpha(common.black, 0.28),
                    '&:hover': {
                        ...base.MuiFilledInput.styleOverrides.root['&:hover'],
                        borderColor: alpha(palette.primary.main, 0.26),
                        backgroundColor: alpha(common.black, 0.24)
                    }
                }
            }
        },
        MuiMenu: {
            ...base.MuiMenu,
            styleOverrides: {
                ...base.MuiMenu.styleOverrides,
                paper: {
                    ...base.MuiMenu.styleOverrides.paper,
                    borderColor: alpha(palette.primary.main, 0.18),
                    boxShadow:
                        `0 0 0 1px ${alpha(palette.primary.main, 0.08)} inset, ` +
                        `0px 0px 32px ${alpha(palette.primary.main, 0.12)}, ` +
                        `0px 20px 44px rgba(0, 0, 0, 0.44)`
                }
            }
        },
        MuiOutlinedInput: {
            ...base.MuiOutlinedInput,
            styleOverrides: {
                ...base.MuiOutlinedInput.styleOverrides,
                root: {
                    ...base.MuiOutlinedInput.styleOverrides.root,
                    backgroundColor: alpha(common.black, 0.18),
                    '&:hover': {
                        ...base.MuiOutlinedInput.styleOverrides.root['&:hover'],
                        backgroundColor: alpha(common.black, 0.12)
                    },
                    '&.Mui-focused': {
                        ...base.MuiOutlinedInput.styleOverrides.root[
                            '&.Mui-focused'
                        ],
                        boxShadow: `0 0 0 1px ${alpha(
                            palette.primary.main,
                            0.12
                        )} inset`
                    }
                },
                notchedOutline: {
                    ...base.MuiOutlinedInput.styleOverrides.notchedOutline,
                    borderColor: alpha(palette.primary.main, 0.14)
                }
            }
        },
        MuiPaper: {
            ...base.MuiPaper,
            styleOverrides: {
                ...base.MuiPaper.styleOverrides,
                root: {
                    ...base.MuiPaper.styleOverrides.root,
                    borderColor: alpha(palette.primary.main, 0.1)
                }
            }
        },
        MuiTableHead: {
            ...base.MuiTableHead,
            styleOverrides: {
                ...base.MuiTableHead.styleOverrides,
                root: {
                    ...base.MuiTableHead.styleOverrides.root,
                    [`& .${tableCellClasses.root}`]: {
                        ...base.MuiTableHead.styleOverrides.root[
                            `& .${tableCellClasses.root}`
                        ],
                        backgroundColor: alpha(palette.primary.main, 0.08),
                        boxShadow: `inset 0 -1px 0 ${alpha(
                            palette.primary.main,
                            0.16
                        )}`
                    }
                }
            }
        },
        MuiTabs: {
            ...base.MuiTabs,
            styleOverrides: {
                ...base.MuiTabs.styleOverrides,
                indicator: {
                    ...base.MuiTabs.styleOverrides.indicator,
                    height: 4,
                    boxShadow:
                        `0px 0px 18px ${alpha(palette.primary.main, 0.5)}, ` +
                        `0px 2px 6px ${alpha(palette.primary.main, 0.3)}`
                }
            }
        },
        MuiTooltip: {
            ...base.MuiTooltip,
            styleOverrides: {
                ...base.MuiTooltip.styleOverrides,
                tooltip: {
                    ...base.MuiTooltip.styleOverrides.tooltip,
                    borderColor: alpha(palette.primary.main, 0.18),
                    background: alpha(common.black, 0.92),
                    boxShadow: `0 0 0 1px ${alpha(
                        palette.primary.main,
                        0.08
                    )} inset`
                }
            }
        }
    }
}
